// =============================================================================
// Admin Email Campaigns Page (Phase II: Fundraising CRM)
// =============================================================================

import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  EnvelopeIcon,
  ClockIcon,
  PaperAirplaneIcon,
  DocumentDuplicateIcon,
  TrashIcon,
  ChartBarIcon,
  ShieldExclamationIcon,
} from '@heroicons/react/24/outline';
import { Card, Button } from '../../../components/ui';
import { fundraisingService } from '../../../services/fundraising.service';
import { getApiError } from '../../../services/api';
import type { EmailCampaign, CampaignStatus } from '../../../types/fundraising';
import { CAMPAIGN_STATUS_LABELS } from '../../../types/fundraising';
import { usePermissions } from '../../../hooks/usePermissions';

// =============================================================================
// Status Badge Colors
// =============================================================================

const STATUS_COLORS: Record<CampaignStatus, string> = {
  DRAFT: 'bg-gray-100 text-gray-700',
  SCHEDULED: 'bg-blue-100 text-blue-700',
  SENDING: 'bg-yellow-100 text-yellow-700',
  SENT: 'bg-green-100 text-green-700',
  CANCELLED: 'bg-red-100 text-red-700',
};

// =============================================================================
// Component
// =============================================================================

export function CampaignsPage() {
  const navigate = useNavigate();
  const { hasPermission, isLoaded: permsLoaded } = usePermissions();
  const canCreate = hasPermission('fundraising', 'create');
  const canDelete = hasPermission('fundraising', 'delete');
  const [isLoading, setIsLoading] = useState(true);
  const [campaigns, setCampaigns] = useState<EmailCampaign[]>([]);
  const [statusFilter, setStatusFilter] = useState<CampaignStatus | ''>('');
  const [error, setError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<EmailCampaign | null>(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  });

  // Stats
  const [stats, setStats] = useState({
    total: 0,
    drafts: 0,
    sent: 0,
    totalRecipients: 0,
  });

  const loadCampaigns = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await fundraisingService.listCampaigns(
        pagination.page,
        pagination.limit,
        statusFilter || undefined
      );

      setCampaigns(result.campaigns);
      setPagination(result.pagination);

      // Calculate stats
      const drafts = result.campaigns.filter((c) => c.status === 'DRAFT').length;
      const sent = result.campaigns.filter((c) => c.status === 'SENT').length;
      const totalRecipients = result.campaigns.reduce((sum, c) => sum + c.totalSent, 0);

      setStats({
        total: result.pagination.total,
        drafts,
        sent,
        totalRecipients,
      });
    } catch (err) {
      console.error('Failed to load campaigns:', err);
      setError(getApiError(err).message);
    } finally {
      setIsLoading(false);
    }
  }, [pagination.page, pagination.limit, statusFilter]);

  useEffect(() => {
    loadCampaigns();
  }, [loadCampaigns]);

  const handleCreateCampaign = async () => {
    try {
      const campaign = await fundraisingService.createCampaign({
        name: 'New Campaign',
        subject: '',
        bodyHtml: '',
      });
      navigate(`/admin/fundraising/campaigns/${campaign.id}`);
    } catch (err) {
      console.error('Failed to create campaign:', err);
      setError(getApiError(err).message);
    }
  };

  const handleDuplicate = async (campaignId: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      const campaign = await fundraisingService.duplicateCampaign(campaignId);
      navigate(`/admin/fundraising/campaigns/${campaign.id}`);
    } catch (err) {
      console.error('Failed to duplicate campaign:', err);
      setError(getApiError(err).message);
    }
  };

  const handleDeleteClick = (campaign: EmailCampaign, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDeleteTarget(campaign);
    setDeleteConfirmText('');
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget || deleteConfirmText.toLowerCase() !== 'proceed') return;
    setDeleting(true);
    try {
      await fundraisingService.deleteCampaign(deleteTarget.id);
      setDeleteTarget(null);
      loadCampaigns();
    } catch (err) {
      console.error('Failed to delete campaign:', err);
      setError(getApiError(err).message);
    } finally {
      setDeleting(false);
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Permission guard - early return after all hooks
  if (permsLoaded && !hasPermission('fundraising', 'view')) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <ShieldExclamationIcon className="h-12 w-12 text-gray-400 mb-3" />
        <h3 className="text-lg font-medium text-gray-900">Access Denied</h3>
        <p className="mt-1 text-sm text-gray-500">You do not have permission to view this page.</p>
      </div>
    );
  }

  if (isLoading && campaigns.length === 0) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-8 w-48 bg-gray-200 rounded" />
        <div className="grid grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-24 bg-gray-200 rounded-xl" />
          ))}
        </div>
        <div className="h-96 bg-gray-200 rounded-xl" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Email Campaigns</h1>
          <p className="text-sm text-gray-500 mt-1">
            Create and send targeted email campaigns to your contacts
          </p>
        </div>
        {canCreate && (
          <Button onClick={handleCreateCampaign} className="flex items-center gap-2">
            <PlusIcon className="h-5 w-5" />
            Create Campaign
          </Button>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card padding="sm">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gray-100 rounded-lg">
              <EnvelopeIcon className="h-5 w-5 text-gray-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Total Campaigns</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
            </div>
          </div>
        </Card>
        <Card padding="sm">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <ClockIcon className="h-5 w-5 text-yellow-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Drafts</p>
              <p className="text-2xl font-bold text-gray-900">{stats.drafts}</p>
            </div>
          </div>
        </Card>
        <Card padding="sm">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <PaperAirplaneIcon className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Sent</p>
              <p className="text-2xl font-bold text-gray-900">{stats.sent}</p>
            </div>
          </div>
        </Card>
        <Card padding="sm">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <ChartBarIcon className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Emails Sent</p>
              <p className="text-2xl font-bold text-gray-900">{stats.totalRecipients.toLocaleString()}</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Filters */}
      <Card padding="sm">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
            <span className="text-sm text-gray-500">Filter by status:</span>
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as CampaignStatus | '')}
            className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="">All Campaigns</option>
            {(Object.keys(CAMPAIGN_STATUS_LABELS) as CampaignStatus[]).map((status) => (
              <option key={status} value={status}>
                {CAMPAIGN_STATUS_LABELS[status]}
              </option>
            ))}
          </select>
        </div>
      </Card>

      {/* Error */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {/* Campaigns List */}
      <Card padding="none">
        {campaigns.length === 0 ? (
          <div className="text-center py-12">
            <EnvelopeIcon className="h-12 w-12 text-gray-300 mx-auto" />
            <h3 className="mt-4 text-lg font-medium text-gray-900">No campaigns yet</h3>
            <p className="mt-2 text-sm text-gray-500">
              Get started by creating your first email campaign.
            </p>
            {canCreate && (
              <Button onClick={handleCreateCampaign} className="mt-4">
                <PlusIcon className="h-5 w-5 mr-2" />
                Create Campaign
              </Button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50">
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Campaign</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Status</th>
                  <th className="text-center py-3 px-4 text-sm font-medium text-gray-500">Recipients</th>
                  <th className="text-center py-3 px-4 text-sm font-medium text-gray-500">Open Rate</th>
                  <th className="text-center py-3 px-4 text-sm font-medium text-gray-500">Click Rate</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Created</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-gray-500">Actions</th>
                </tr>
              </thead>
              <tbody>
                {campaigns.map((campaign) => {
                  const openRate = campaign.totalSent > 0
                    ? Math.round((campaign.totalOpened / campaign.totalSent) * 100)
                    : 0;
                  const clickRate = campaign.totalSent > 0
                    ? Math.round((campaign.totalClicked / campaign.totalSent) * 100)
                    : 0;

                  return (
                    <tr
                      key={campaign.id}
                      className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer"
                      onClick={() => navigate(`/admin/fundraising/campaigns/${campaign.id}`)}
                    >
                      <td className="py-3 px-4">
                        <div>
                          <p className="text-sm font-medium text-gray-900">{campaign.name}</p>
                          {campaign.subject && (
                            <p className="text-xs text-gray-500 truncate max-w-xs">{campaign.subject}</p>
                          )}
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[campaign.status]}`}>
                          {CAMPAIGN_STATUS_LABELS[campaign.status]}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-center text-sm text-gray-500">
                        {campaign.totalSent > 0 ? campaign.totalSent.toLocaleString() : '-'}
                      </td>
                      <td className="py-3 px-4 text-center text-sm text-gray-500">
                        {campaign.status === 'SENT' ? `${openRate}%` : '-'}
                      </td>
                      <td className="py-3 px-4 text-center text-sm text-gray-500">
                        {campaign.status === 'SENT' ? `${clickRate}%` : '-'}
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-500">
                        {formatDate(campaign.createdAt)}
                      </td>
                      <td className="py-3 px-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          {canCreate && (
                            <button
                              onClick={(e) => handleDuplicate(campaign.id, e)}
                              className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-400 hover:text-gray-600"
                              title="Duplicate"
                            >
                              <DocumentDuplicateIcon className="h-4 w-4" />
                            </button>
                          )}
                          {campaign.status === 'DRAFT' && canDelete && (
                            <button
                              onClick={(e) => handleDeleteClick(campaign, e)}
                              className="p-1.5 hover:bg-red-50 rounded-lg text-gray-400 hover:text-red-600"
                              title="Delete"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200">
            <p className="text-sm text-gray-500">
              Showing {(pagination.page - 1) * pagination.limit + 1} to{' '}
              {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total}
            </p>
            <div className="flex gap-2">
              <Button
                variant="secondary"
                size="sm"
                disabled={pagination.page === 1}
                onClick={() => setPagination((p) => ({ ...p, page: p.page - 1 }))}
              >
                Previous
              </Button>
              <Button
                variant="secondary"
                size="sm"
                disabled={pagination.page === pagination.totalPages}
                onClick={() => setPagination((p) => ({ ...p, page: p.page + 1 }))}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Delete Confirmation Modal */}
      {deleteTarget && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-red-100 rounded-full">
                <TrashIcon className="w-6 h-6 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Delete Campaign</h3>
            </div>

            <p className="text-sm text-gray-600 mb-2">
              You are about to permanently delete this campaign:
            </p>
            <p className="text-sm font-medium text-gray-900 mb-4 p-2 bg-gray-50 rounded-lg">
              {deleteTarget.name}
            </p>
            <p className="text-sm text-red-600 mb-4">
              This action cannot be undone.
            </p>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Type <span className="font-bold">proceed</span> below to confirm deletion:
              </label>
              <input
                type="text"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder="Type 'proceed' to confirm"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                autoFocus
              />
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setDeleteTarget(null);
                  setDeleteConfirmText('');
                }}
                className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteConfirm}
                disabled={deleteConfirmText.toLowerCase() !== 'proceed' || deleting}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {deleting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                    Deleting...
                  </>
                ) : (
                  'Delete Campaign'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CampaignsPage;
